import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { SoundControls } from "@/components/SoundControls";

const TypeDuel = lazy(() => import("@/components/games/TypeDuel").then((m) => ({ default: m.TypeDuel })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld Type Chart Quiz — 60-Second Duel";
const DESCRIPTION =
  "Free Palworld type chart quiz: call the damage multiplier for random element matchups in 60 seconds, learn every weakness fast and share your score.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Start the timer", text: "Press start and the 60-second clock begins with the first attacker-versus-defender matchup." },
  { name: "Call the multiplier", text: "Answer ×0.5, ×1 or ×2 for each pairing — the values come from the same elemental chart the damage calculator uses." },
  { name: "Learn the misses", text: "A wrong answer prints the correct multiplier immediately, which is how the drill teaches the chart." },
  { name: "Share the score", text: "When the timer stops you get your total and a share link, and badges unlock at 10 and 20 correct." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld type chart quiz?", a: "It is a free 60-second drill on Pal Stars: random Palworld element matchups appear one after another and you call the damage multiplier — ×0.5, ×1 or ×2 — as fast as you can." },
  { q: "Where do the multipliers come from?", a: "Straight from the Pal Stars elemental chart used by the damage calculator: Fire beats Grass and Ice, Water beats Fire and Ground, Grass beats Water and Ground, Electric beats Water, Ice beats Grass and Dragon, Ground beats Fire and Electric, Dark beats Neutral, and Dragon beats Dragon." },
  { q: "Is a neutral matchup always ×1?", a: "Yes. Any pairing that is not listed as strong or resisted resolves to ×1, and Neutral attacks have no strong matchups at all." },
  { q: "Does the type duel pay rewards?", a: "No. It is cosmetic: your best score is stored in your browser, badges unlock at 10 and 20 correct answers, and runs are capped at 25 per UTC day." },
  { q: "How can I learn the Palworld type chart faster?", a: "Run the duel twice, then read the full Palworld elemental chart and run it again — most players jump five to ten answers after one pass over the chart." },
];

export const Route = createFileRoute("/type-duel")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Type Chart Duel — 60 seconds of matchups" },
      { property: "og:description", content: "Rapid-fire Palworld element matchups: call ×0.5, ×1 or ×2 before the timer runs out." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/type-duel" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/type-duel" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Type Chart Duel", "/type-duel"]]),
      toolSchema({ name: "Palworld Type Chart Duel", description: DESCRIPTION, path: "/type-duel" }),
      howToSchema({ name: "How to play the Palworld type chart quiz", description: "Start the timer, call each multiplier, learn the misses and share your score.", path: "/type-duel", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/type-duel"),
    ],
  }),
  component: TypeDuelPage,
});

function TypeDuelPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · 60-second game</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld type chart duel</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Palworld type chart quiz throws random element matchups at you for 60 seconds: answer ×0.5, ×1 or ×2 and
          the drill corrects every miss instantly, so the whole weakness chart sticks after a few runs.
        </p>
        <SoundControls className="mt-4 justify-start" label="duel sound" />
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld type chart duel</h2><p className="mt-2 text-sm text-muted-foreground">Loading the duel…</p></section>}>
        <TypeDuel />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="type-duel" heading="Type duel badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/elements" className="font-semibold text-primary hover:underline">Palworld elemental chart →</Link> the full weakness table.</li>
          <li><a href="/damage" className="font-semibold text-primary hover:underline">Palworld damage calculator →</a></li>
          <li><Link to="/whos-that-pal" className="font-semibold text-primary hover:underline">Who's That Pal? quiz →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld type chart quiz FAQ" />
    </div>
  );
}
